import { useCallback, useEffect, useState } from "react";
import { Edit3, Save, X } from "lucide-react";
import { userApi } from "../../backend/client";
import type { UserRecord } from "../../backend/api/users";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export type ProjectCustomerSummary = {
  id?: string;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  contactnumber?: string | null;
};

type CustomerDraft = {
  firstname: string;
  lastname: string;
  email: string;
  contactnumber: string;
};

type ProjectCustomerCardProps = {
  customer: ProjectCustomerSummary | null;
  canEdit: boolean;
  onCustomerUpdated?: (customer: UserRecord) => void;
};

const draftFor = (customer: ProjectCustomerSummary | null): CustomerDraft => ({
  firstname: customer?.firstname || "",
  lastname: customer?.lastname || "",
  email: customer?.email || "",
  contactnumber: customer?.contactnumber || "",
});

export function ProjectCustomerCard({
  customer,
  canEdit,
  onCustomerUpdated,
}: ProjectCustomerCardProps) {
  const [details, setDetails] = useState<UserRecord | null>(null);
  const [draft, setDraft] = useState<CustomerDraft>(() => draftFor(customer));
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadCustomer = useCallback(async () => {
    if (!canEdit || !customer?.id) return;

    setLoading(true);
    setError("");
    const { data, error: requestError } = await userApi.get(customer.id);
    setLoading(false);

    if (requestError || !data) {
      setError(requestError?.message || "Could not load customer details.");
      return;
    }

    setDetails(data);
    setDraft(draftFor(data));
  }, [canEdit, customer?.id]);

  useEffect(() => {
    let active = true;

    if (!canEdit || !customer?.id) {
      setDetails(null);
      setDraft(draftFor(customer));
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError("");
    userApi.get(customer.id).then(({ data, error: requestError }) => {
      if (!active) return;
      setLoading(false);

      if (requestError || !data) {
        setError(requestError?.message || "Could not load customer details.");
        return;
      }

      setDetails(data);
      setDraft(draftFor(data));
    });

    return () => {
      active = false;
    };
  }, [canEdit, customer]);

  const displayedCustomer = details || customer;
  const fullName = displayedCustomer
    ? [displayedCustomer.firstname, displayedCustomer.lastname]
        .filter(Boolean)
        .join(" ")
    : "";

  const cancelEditing = () => {
    setDraft(draftFor(displayedCustomer));
    setError("");
    setEditing(false);
  };

  const saveCustomer = async () => {
    if (!details?.id || saving) return;

    setSaving(true);
    setError("");
    const { data, error: requestError } = await userApi.update(details.id, {
      expected_version: details.version,
      firstname: draft.firstname.trim(),
      lastname: draft.lastname.trim(),
      email: draft.email.trim(),
      contactnumber: draft.contactnumber.trim() || null,
    });
    setSaving(false);

    if (requestError || !data) {
      setError(requestError?.message || "Could not update this customer.");
      return;
    }

    setDetails(data);
    setDraft(draftFor(data));
    setEditing(false);
    onCustomerUpdated?.(data);
  };

  return (
    <Card className="h-full">
      <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
        <div>
          <CardTitle>Customer</CardTitle>
          <CardDescription>Primary transferee details.</CardDescription>
        </div>
        {canEdit && customer?.id && (
          <div className="flex shrink-0 gap-2">
            {editing ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={cancelEditing}
                  disabled={saving}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void saveCustomer()}
                  disabled={saving || !details}
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Saving..." : "Save"}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setError("");
                  if (!details) void loadCustomer();
                  setEditing(true);
                }}
                disabled={loading}
              >
                <Edit3 className="h-4 w-4" />
                Edit customer
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {error && (
          <p className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {editing ? (
          <div className="grid gap-4">
            <CustomerInput
              id="customer-first-name"
              label="First name"
              value={draft.firstname}
              onChange={(value) =>
                setDraft((previous) => ({ ...previous, firstname: value }))
              }
            />
            <CustomerInput
              id="customer-last-name"
              label="Last name"
              value={draft.lastname}
              onChange={(value) =>
                setDraft((previous) => ({ ...previous, lastname: value }))
              }
            />
            <CustomerInput
              id="customer-email"
              label="Email"
              type="email"
              value={draft.email}
              onChange={(value) =>
                setDraft((previous) => ({ ...previous, email: value }))
              }
            />
            <CustomerInput
              id="customer-contact-number"
              label="Contact number"
              type="tel"
              value={draft.contactnumber}
              onChange={(value) =>
                setDraft((previous) => ({
                  ...previous,
                  contactnumber: value,
                }))
              }
            />
          </div>
        ) : (
          <div className="divide-y">
            <CustomerValue
              label="Name"
              value={loading ? "Loading..." : fullName || "N/A"}
            />
            <CustomerValue
              label="Email"
              value={loading ? "Loading..." : displayedCustomer?.email || "N/A"}
            />
            <CustomerValue
              label="Contact Number"
              value={
                loading
                  ? "Loading..."
                  : displayedCustomer?.contactnumber || "N/A"
              }
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CustomerInput({
  id,
  label,
  value,
  onChange,
  type = "text",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email" | "tel";
}) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        className="mt-2"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function CustomerValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <p className="text-xs font-medium uppercase text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 break-words text-sm font-medium">{value}</p>
    </div>
  );
}
